import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import {FiZap, FiTarget, FiRefreshCw, FiTrendingUp} from 'react-icons/fi';

const reasons = [
  {
    icon: FiTarget,
    title: "A Plan Built Around Your Biology",
    body: "Generic diets fail because they ignore the individual. Your protocol is built around your medical history, metabolism, and lifestyle so that every recommendation actually fits your real life."
  },
  {
    icon: FiZap,
    title: "Prescription Support When You Qualify",
    body: "For clients who qualify medically, FDA-approved weight-loss medications may be prescribed by independent, licensed physicians and shipped directly to your door. Eligibility is determined solely by your treating provider."
  },
  {
    icon: FiRefreshCw,
    title: "Accountability That Catches You Before You Fall",
    body: "Most programs leave you alone after day one. Our coaches review your logs every single day and reach out before a rough patch turns into a full restart."
  },
  {
    icon: FiTrendingUp,
    title: "Weekly Calibrations Keep You Moving",
    body: "Every week your coach reviews your weigh-in and adjusts the plan. If the scale stalls, we know why and we fix it. You never spend two weeks spinning your wheels."
  },
];

const WhyItWorks = () => (
  <section className="py-20 md:py-32 bg-[#faf8f5] relative">
    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-stone-200 to-transparent" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{opacity: 0, y: 20}}
        whileInView={{opacity: 1, y: 0}}
        viewport={{once: true}}
        className="text-center max-w-3xl mx-auto mb-16 md:mb-24"
      >
        <span className="inline-block px-5 py-2 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-sm font-bold uppercase tracking-widest mb-6">
          Why It Works
        </span>
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-stone-900 tracking-tight font-serif leading-tight mb-6">
          Built to Fix the Reasons<br />
          <span className="italic font-light text-stone-400">You've Struggled Before</span>
        </h2>
        <p className="text-xl md:text-2xl text-stone-500 font-light leading-relaxed">
          Every part of this program exists because something in the traditional model was broken.
        </p>
      </motion.div>
      <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
        {reasons.map((r, i) => (
          <motion.div
            key={i}
            initial={{opacity: 0, y: 24}}
            whileInView={{opacity: 1, y: 0}}
            viewport={{once: true, margin: '-40px'}}
            transition={{delay: i * 0.1, duration: 0.6}}
            className="flex gap-6 p-8 md:p-10 bg-white rounded-[2rem] border border-stone-100 hover:border-amber-200 hover:shadow-xl hover:shadow-amber-900/5 transition-all duration-500"
          >
            <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-stone-900 flex items-center justify-center">
              <SafeIcon icon={r.icon} className="text-amber-400 text-xl" />
            </div>
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-stone-900 font-serif mb-3 leading-tight">{r.title}</h3>
              <p className="text-lg md:text-xl text-stone-500 font-light leading-relaxed">{r.body}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export default WhyItWorks;